import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useConfigurationCityListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityListAPI";
import { useQuickCommercePincodeListAPI } from "@/app/hooks/api_hooks/quick_commerce/useQuickCommercePincodeListAPI";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { setPincodeList } from "@/app_redux/reducers/slices/auth/quick_commerce_slice";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

const QuickCommercePincodeSetup = () => {
  const formHook = useForm();
  let pincodeFetched = false;
  const { getRouteKey } = useRouter();
  const isPincodeListInProgress = useLoader("isOnboardPincodeListInProgress");
  const navigate = useNavigate();
  const [params, _setparams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { execute: fetchPincodeList, pincodeList } = useQuickCommercePincodeListAPI();
  const routeIdConfig = getRouteKey("CONFIGURATION", "id");
  const { routeState } = useRouteInfo(routeIdConfig);
  // const { pincodeList: pincodeList } = routeState.state as IQuickCommerceListState;
  const fetchCities = useConfigurationCityListAPI();
  const { cityList } = routeState?.state as IConfigurationListState;
  const cityOrgItem = useSelectMenuReducer(cityList, "name", "id");

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    const paramsCityId = params?.get("city_id");
    if (paramsCityId && !pincodeFetched) {
      fetchPincodeList(parseInt(paramsCityId));
      pincodeFetched = true;
    }
  }, [cityList]);

  const clearError = (fieldName) => {
    formHook?.clearErrors(fieldName);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 mt-10 bg-white py-3 rounded-md">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">Pincodes</h1>
          <p className="mt-2 text-sm text-gray-700">A list of all the pincodes onboarded in the VMS.</p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none flex gap-3 items-center">
          <div className="h-9 w-[300px]">
            <NonSearchableSelectMenu
              selectItems={cityOrgItem}
              onSelectItem={(selectedCity) => {
                const cityId = selectedCity?.id;
                const cityIdAsNumber = cityId ? parseInt(cityId, 10) : undefined;
                formHook.setValue("cities", cityIdAsNumber);
                clearError("cities");
                if (cityIdAsNumber) {
                  fetchPincodeList(cityIdAsNumber);
                }
              }}
              placeholder="Select Cities"
              className="text-xs"
              showDropdownIcon={true}
              errorMessages={[{ type: "required", message: "City is required." }]}
              fieldError={formHook.formState.errors.cities}
              register={formHook.register("cities", { required: true })}
            />
          </div>

          <SolidButton asLink href={getRouteKey("PINCODE_ONBOARD", "url")} size="XL">
            Onboard New Pincode
          </SolidButton>
        </div>
      </div>
      {isPincodeListInProgress ? (
        <div className="mt-4">
          <SkeletonType1 />
          <SkeletonType1 className="mt-8" />
        </div>
      ) : pincodeList?.length > 0 ? (
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-8 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                      SN
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Pincode
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Latitude
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Longitude
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      City
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pincodeList?.map((pincode, idx) => {
                    return (
                      <tr key={pincode?.pincode}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                          {idx + 1}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{pincode?.pincode}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {pincode?.latitude ? pincode?.latitude : "-"}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {pincode?.longitude ? pincode?.longitude : "-"}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{pincode?.city}</td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                          <a
                            onClick={(event) => {
                              event.preventDefault();
                              dispatch(
                                setPincodeList({
                                  data: [],
                                }),
                              );
                              navigate(`${getRouteKey("PINCODE_ONBOARD", "url")}?pincode_id=${pincode?.pincode}`);
                            }}
                            href="#"
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            Edit
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <NoDataFoundComponent
          message="No pincode have been onboarded"
          description="No pincode data is available try onboarding a pincode"
        />
      )}
    </div>
  );
};

export default QuickCommercePincodeSetup;
